import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Info, Compass, Banknote, ShieldCheck,
  Activity, UploadCloud, Trash2, Globe, MapPin, Tag,
  Calendar, Clock, FileText
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { ExperienceDto, BookingDto, ExperienceDetailsDto } from "@/types";
import { Select, message } from "antd";
import InstrumentLoader from "@/components/InstrumentLoader";
import TimeLogAltimeterButton from "@/components/TimeLogAltimeterButton";
import ExperienceCalendarModal from "@/components/ExperienceCalendarModal";
import { EXPERIENCE_CATEGORIES } from "../enums";
import ConfirmationAlert from "@/components/ConfirmationAlert";

const GlassCard = ({ children, className = "", title, icon: Icon }: any) => (
  <div className={`bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl flex flex-col h-full ${className}`}>
    {title && (
      <div className="flex items-center gap-2 mb-5 border-b border-slate-800/50 pb-4">
        {Icon && <Icon size={18} className="text-red-500" />}
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-white">{title}</h3>
      </div>
    )}
    <div className="flex-1">{children}</div>
  </div>
);

const InputField = "w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white placeholder:text-slate-500 text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all outline-none";
const Label = "text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block ml-1";
const commonSelectProps = {
  variant: "borderless" as const,
  className: "w-full h-11 bg-[#1E293B] border border-[#334155] rounded-xl text-white",
  dropdownStyle: { backgroundColor: '#0F172A', border: '1px solid #334155', color: 'white' },
  styles: {
    selection: { color: 'white', paddingLeft: '8px' },
    input: { color: 'white' }
  }
};

const Experience = () => {
  const { saveExperience, updateExperience  } = useAppContext() as any;
  const { experienceCommissionRate, syncSystemCommissionRates, getDictValue } = useAuth() as any;
  const { params, navigateTo } = useNavigation();
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempBookings, setTempBookings] = useState<BookingDto[]>([]);
  const [removedBookingIds, setRemovedBookingIds] = useState<number[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDraftAlertOpen, setIsDraftAlertOpen] = useState(false);

  const isInitialMount = useRef(true);
  const experienceFromParams = params?.experience as ExperienceDto | undefined;
  const isEditMode = !!experienceFromParams;

  const [existingMedia, setExistingMedia] = useState<{id: number, url: string}[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [price, setPrice] = useState<number | "">("");
  const [commissionModel, setCommissionModel] = useState("DEDUCT_FROM_PRICE");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState<Partial<ExperienceDto>>({
    type: undefined,
    title: "",
    description: "",
    location: "",
    calendarPayloadDto: undefined,
    durationInMinutes: 60,
    timeInterval: "06:00 - 22:00"
  });

  const COMMISSION_MODELS = [
    { code: "DEDUCT_FROM_PRICE", value: "Deduce din prețul total (Comision inclus)" },
    { code: "ADD_ON_TOP", value: "Adaugă peste preț (Comision adăugat peste)" }
  ];

  const alertDict = {
      title: "Verificare Necesară",
      subtitle: isEditMode ? "Re-evaluare Experiență" : "Validare Experiență Aviatică",
      description: isEditMode
        ? "Ai ales să modifici datele unei experiențe active. Pentru a asigura conformitatea standardelor platformei, orice actualizare trimite pachetul înapoi în lista de verificare."
        : "Evenimentul tău este aproape gata pentru decolare! Înainte de a fi publicată oficial pe marketplace, experiența va trece printr-o scurtă validare tehnică a detaliilor introduse.",
      info: isEditMode
        ? [
            "Experiența va fi temporar indisponibilă pentru rezervări noi în timpul re-verificării.",
            "Echipa operațională va valida noul tarif brut și descrierea actualizată.",
            "După aprobare, pachetul va fi relistat automat cu noile specificații."
          ]
        : [
            "Dispeceratul nostru va revizui locația, aerodromul și tipul activității.",
            "Se va verifica corectitudinea intervalelor introduse în calendarul de disponibilitate.",
            "Odată aprobată, clienții vor putea rezerva direct sloturile orare alocate."
          ],
      btnCancel: "Înapoi la formular",
      btnConfirm: "Am înțeles, publică"
    };

  const draftAlertDict = {
    title: "SALVARE ÎN MOD DRAFT",
    subtitle: "Listarea nu va fi publică",
    description: "Anunțul va fi salvat în contul tău fără a fi trimis spre verificare.",
    info: [
      "Această experiență NU va fi vizibilă public pe marketplace.",
      "Oricând poți reveni pentru a completa datele lipsă și a o trimite spre aprobare.",
      "Anunțul va rămâne salvat în siguranță în panoul tău de administrare."
    ],
    btnCancel: "Înapoi la editare",
    btnConfirm: "Confirmă Salvare Draft"
  };

   useEffect(() => {
              if (syncSystemCommissionRates) {
                syncSystemCommissionRates().catch((err: any) => {
                  console.error("Nu s-au putut sincroniza comisioanele globale la montarea paginii:", err);
                });
              }
            }, [syncSystemCommissionRates]);

  const computedValues = useMemo(() => {
    const inputPrice = Number(price) || 0;
    const rate = Number(experienceCommissionRate) || 0;

    if (commissionModel === "DEDUCT_FROM_PRICE") {
      const grossValue = inputPrice;
      const commissionValue = grossValue * rate;
      const netValue = grossValue - commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    } else {
      const netValue = inputPrice;
      const commissionValue = netValue * rate;
      const grossValue = netValue + commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    }
  }, [price, commissionModel, experienceCommissionRate]);

  const totalDays = useMemo(() => {
    const slots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    const dates = new Set(slots.map(s => {
      if (!s.from) return "";
      const { year, month, day } = s.from;
      return new Date(year, (month || 1) - 1, day || 1).toDateString();
    }).filter(Boolean));
    return dates.size;
  }, [formData.calendarPayloadDto]);

  const totalSlots = useMemo(() => {
    const slots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    return slots.length;
  }, [formData.calendarPayloadDto]);

  const isNotEmpty = (val: any) => val !== null && val !== undefined && val.toString().trim() !== "";

  const hasMedia = existingMedia.length > 0 || mediaFiles.length > 0;

  const checkFormValidity = () => {
    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    const mandatoryPresent =
      hasMedia &&
      isNotEmpty(formData.title) &&
      isNotEmpty(formData.description) &&
      isNotEmpty(formData.type) &&
      isNotEmpty(formData.location) &&
      timeSlots.length > 0;

    const isPriceValid = isNotEmpty(price) && Number(price) > 0;
    return isPriceValid && mandatoryPresent;
  };

  const isFormValid = checkFormValidity();

  useEffect(() => {
    if (experienceFromParams && isInitialMount.current) {
      setFormData(experienceFromParams);
      if (experienceFromParams.mediaUrls && experienceFromParams.mediaUrls.length > 0) {
        const mappedUrls = experienceFromParams.mediaUrls.map((urlStr, index) => ({
          id: index,
          url: urlStr
        }));
        setExistingMedia(mappedUrls);
      } else if (experienceFromParams.media) {
        const mapped = experienceFromParams.media.map((m: any, index: number) => ({
          id: index,
          url: m.mediaData.startsWith('data:') ? m.mediaData : `data:image/jpeg;base64,${m.mediaData}`
        }));
        setExistingMedia(mapped);
      }
      setCommissionModel(experienceFromParams.commissionModel || "DEDUCT_FROM_PRICE");
      if (experienceFromParams.commissionModel === "ADD_ON_TOP") {
        setPrice(experienceFromParams.netValue || "");
      } else {
        setPrice(experienceFromParams.grossValue || "");
      }
      isInitialMount.current = false;
    }
  }, [experienceFromParams]);

  useEffect(() => {
      return () => {
        setRemovedBookingIds([]);
        setTempBookings([]);
        setTouched({});
      };
    }, []);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setMediaFiles([...mediaFiles, ...Array.from(e.target.files)]);
  };

  const removeExistingImage = (id: number) => {
    setExistingMedia(prev => prev.filter(img => img.id !== id));
  };

  const removeNewFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const executeSubmit = async (isDraftMode = false) => {
      setIsAlertOpen(false);
      setIsDraftAlertOpen(false);
      setLoading(true);
      try {
        const experienceDto: ExperienceDto = {
          experienceId: isEditMode ? experienceFromParams?.experienceId : undefined,
          sellerId: isEditMode ? experienceFromParams?.sellerId : undefined,
          type: formData.type,
          title: formData.title,
          description: formData.description,
          location: formData.location,
          commissionModel: commissionModel,
          grossValue: computedValues.grossValue,
          media: mediaFiles,
          mediaUrls: existingMedia.map((m) => m.url),
          draft: isDraftMode,
          active: !isDraftMode,
          verified: false,
          experienceDetailsId: isEditMode ? experienceFromParams?.experienceDetailsId : undefined
        };

        const experienceDetailsDto: ExperienceDetailsDto = {
          durationInMinutes: formData.durationInMinutes ? Number(formData.durationInMinutes) : undefined,
          timeInterval: formData.timeInterval || "",
          commissionRate: computedValues.rate,
          commissionValue: computedValues.commissionValue,
          netValue: computedValues.netValue,
          chargingModel: "BY_COMMISSION",
          calendarPayloadDto: formData.calendarPayloadDto
        };

        if (isEditMode) {
          const success = await updateExperience(experienceDto, experienceDetailsDto, removedBookingIds);
          if (success) {
            message.success("Modificări salvate cu succes.");
            setRemovedBookingIds([]);
            navigateTo("dashboard");
          } else {
            message.error("Eroare la actualizarea experienței.");
          }
        } else {
          const success = await saveExperience(experienceDto, experienceDetailsDto);
          if (success) {
            message.success("Experiența aviatică a fost publicată.");
            navigateTo("dashboard");
          } else {
            message.error("Eroare la publicarea experienței.");
          }
        }
      } catch (err) {
        console.error(err);
        message.error("Eroare la procesarea datelor.");
      } finally {
        setLoading(false);
      }
    };

  const validateAndScroll = () => {
    setIsSubmitted(true);
    setTouched({ bookings: true });

    if (!isNotEmpty(formData.title) || !isNotEmpty(formData.type) || !isNotEmpty(formData.location)) {
      document.getElementById("general-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!hasMedia) {
      document.getElementById("media-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!isNotEmpty(price) || Number(price) <= 0) {
      document.getElementById("finance-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!isNotEmpty(formData.description)) {
      document.getElementById("description-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    if (timeSlots.length === 0) {
      document.getElementById("bookings-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    return true;
  };

  const handleDraftClick = () => {
    setIsDraftAlertOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateAndScroll();

    if (isValid) {
      setIsAlertOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={loading} />

      <div className="container max-w-7xl py-12 px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-red-600 pl-6">
          <div>
            <p className="text-red-500 font-mono text-sm tracking-[0.2em] mb-1 uppercase italic font-black tracking-widest">
              FLIGHT EXPERIENCES // {isEditMode ? "EXPERIENCE_UPDATE" : "EXPERIENCE_REGISTRATION"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight">
              {isEditMode ? "Editare" : "Înregistrare"} <span className="text-slate-500">Experiență</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">System</p>
              <p className="text-red-500 font-mono text-xs font-bold uppercase">Ready</p>
            </div>
            <div className="text-right px-4">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Status</p>
              <p className="text-orange-500 font-mono text-xs font-bold flex items-center gap-1">
                <Activity size={12} className="animate-pulse" /> AWAITING_INPUT
              </p>
            </div>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-8">
            <div id="general-section">
              <GlassCard title="Detalii Experiență Aviatică" icon={Compass}>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className={Label}>Titlu Experiență <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !isNotEmpty(formData.title) ? "border-red-500 ring-1 ring-red-500 shadow-[0_0_10px_rgba(239,68,68,0.1)]" : ""}`} placeholder="Ex: Zbor introductiv Copilot pentru o zi sau Salt în tandem" value={formData.title || ""} onChange={e => setFormData({...formData, title: e.target.value})} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={Label}>Tipul Activității <span className="text-red-500">*</span></label>
                      <Select
                        {...commonSelectProps}
                        className={`${commonSelectProps.className} ${isSubmitted && !isNotEmpty(formData.type) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        placeholder={<span className="text-slate-500">Alege tip activitate...</span>}
                        value={formData.type || undefined}
                        onChange={val => setFormData({...formData, type: val})}
                        options={EXPERIENCE_CATEGORIES.map(t => ({
                          label: t.label,
                          value: t.code,
                          style: { color: 'white' }
                        }))}
                      />
                    </div>
                    <div>
                      <label className={Label}>Locație / Aerodrom <span className="text-red-500">*</span></label>
                      <input className={`${InputField} ${isSubmitted && !isNotEmpty(formData.location) ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder="Ex: Aerodrom Tuzla, Constanța" value={formData.location || ""} onChange={e => setFormData({...formData, location: e.target.value})} />
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>

            <div id="media-section">
              <GlassCard title="Galerie Foto / Video" icon={UploadCloud}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <label className={Label}>
                      Media Files <span className="text-red-500">*</span>
                    </label>
                    {!hasMedia && (
                      <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight">
                        {getDictValue("experience.media.mandatory")}
                      </span>
                    )}
                  </div>

                  <label className={`group relative border-2 border-dashed transition-all rounded-3xl p-12 flex flex-col items-center justify-center cursor-pointer overflow-hidden ${
                    isSubmitted && !hasMedia
                      ? "border-red-500/80 bg-red-500/5 shadow-[0_0_15px_rgba(239,68,68,0.1)]"
                      : "border-slate-700 hover:border-red-500/50 bg-slate-900/20"
                  }`}>
                    <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity">
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] border border-red-500 rounded-full animate-ping" />
                    </div>
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <UploadCloud className="w-8 h-8 text-red-500" />
                      </div>
                      <h4 className="text-white font-bold mb-1 uppercase tracking-tighter">Scan & Upload Media</h4>
                      <p className="text-xs text-slate-500 tracking-widest font-bold uppercase">Imagini sau Video (MAX 50MB)</p>
                    </div>
                    <input type="file" multiple className="hidden" onChange={handleMediaChange} />
                  </label>

                  {(existingMedia.length > 0 || mediaFiles.length > 0) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {existingMedia.map((img) => (
                        <div className="group relative bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden aspect-video shadow-lg" key={`old-${img.id}`}>
                          <img src={img.url} className="w-full h-full object-cover opacity-80" />
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-red-600 text-[7px] font-black rounded text-white uppercase">Activă</div>
                          <button type="button" onClick={() => removeExistingImage(img.id)} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg"><Trash2 size={12} /></button>
                        </div>
                      ))}
                      {mediaFiles.map((file, i) => (
                        <div className="group relative bg-slate-800 border border-red-500/30 rounded-2xl aspect-video overflow-hidden shadow-lg" key={`new-${i}`}>
                          <img src={URL.createObjectURL(file)} className="w-full h-full object-cover opacity-60" />
                          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-orange-600 text-[7px] font-black rounded text-white uppercase">Upload</div>
                          <button type="button" onClick={() => removeNewFile(i)} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg"><Trash2 size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </GlassCard>
            </div>
          </div>

          <div className="space-y-8">
            <div id="finance-section">
              <GlassCard title="Configurare Gestiune Financiară" icon={Banknote}>
                <div className="space-y-6">
                  <div className="animate-in fade-in duration-300">
                      <label className={Label}>Model Comisionare <span className="text-red-500">*</span></label>
                      <Select
                          {...commonSelectProps}
                          value={commissionModel}
                          onChange={val => setCommissionModel(val)}
                          options={COMMISSION_MODELS.map(m => ({
                              label: <span className="text-white">{m.value}</span>,
                              value: m.code
                          }))}
                      />
                  </div>

                  <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-800 items-center">
                    <div>
                      <label className={Label}>
                        {commissionModel === "DEDUCT_FROM_PRICE" ? "Tarif Experiență Brut Public (EUR)" : "Tarif Experiență Net Dorit (EUR)"} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        className={`bg-transparent text-4xl font-black text-white w-full outline-none placeholder:text-slate-700 mt-1 ${isSubmitted && (!isNotEmpty(price) || Number(price) <= 0) ? "text-red-500" : ""}`}
                        placeholder="000"
                        value={price}
                        onChange={e => setPrice(e.target.value === "" ? "" : parseFloat(e.target.value))}
                      />
                    </div>
                  </div>

                  {Number(price) > 0 && (
                    <div className="bg-red-600/5 border border-red-500/10 rounded-2xl p-5 space-y-3 font-mono text-xs">
                      <div className="flex justify-between text-slate-400">
                        <span>TARIF PUBLIC EXPERIENȚĂ (BRUT):</span>
                        <span className="font-bold text-white text-sm">{computedValues.grossValue.toLocaleString()} EUR</span>
                      </div>
                      <div className="flex justify-between text-slate-400 border-b border-red-500/60 pb-2">
                        <span>REȚINERE COMISION STRUCTURE ({(computedValues.rate * 100).toFixed(1)}%):</span>
                        <span className="font-bold text-red-500">-{computedValues.commissionValue.toLocaleString()} EUR</span>
                      </div>
                      <div className="flex items-center gap-2 bg-red-500/10 p-3 rounded-xl text-red-400 border border-red-500/20">
                        <Info size={16} className="shrink-0" />
                        <div className="flex-1 flex justify-between items-center">
                          <span className="font-bold text-[10px] uppercase tracking-wider">NET DE DECONTAT ÎN CONT (NET):</span>
                          <span className="text-xl font-black text-emerald-400">{computedValues.netValue.toLocaleString()} EUR</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </GlassCard>
            </div>

            <div id="description-section">
              <GlassCard title="Prezentare Detaliată Experiență" icon={Tag}>
                <div className="space-y-2">
                  <label className={Label}>Descriere și ce este inclus <span className="text-red-500">*</span></label>
                  <textarea
                    className={`w-full h-48 p-4 rounded-xl bg-[#1E293B] border text-white text-sm focus:border-red-500 outline-none resize-none transition-all ${isSubmitted && !isNotEmpty(formData.description) ? "border-red-500 ring-1 ring-red-500" : "border-[#334155]"}`}
                    placeholder="Detaliază programul, durata briefing-ului, minutele efective de zbor, dacă filmarea este inclusă, contraindicații medicale etc..."
                    value={formData.description || ""}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                  />
                </div>
              </GlassCard>
            </div>

            <div id="bookings-section" className={(touched.bookings || isSubmitted) && (!formData.calendarPayloadDto?.timeSlotDtoList || formData.calendarPayloadDto.timeSlotDtoList.length === 0) ? "ring-2 ring-red-500 rounded-2xl p-1 bg-red-500/5" : ""}>
              <TimeLogAltimeterButton
                onClick={() => {
                  setIsModalOpen(true);
                  setTouched(prev => ({...prev, bookings: true}));
                }}
                totalDays={totalDays}
                totalSlots={totalSlots}
              />
              {(touched.bookings || isSubmitted) && (!formData.calendarPayloadDto?.timeSlotDtoList || formData.calendarPayloadDto.timeSlotDtoList.length === 0) && (
                <p className="text-[10px] text-red-500 font-bold mt-2 ml-1 uppercase">Sistemul de programări este obligatoriu</p>
              )}
            </div>

            <ExperienceCalendarModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={(experienceDetails) => {
                setFormData(prev => ({
                  ...prev,
                  calendarPayloadDto: experienceDetails.calendarPayloadDto,
                  durationInMinutes: experienceDetails.durationInMinutes,
                  timeInterval: experienceDetails.timeInterval
                }));
                setIsModalOpen(false);
              }}
              registration={formData.title || "N/A"}
              initialExperienceDetails={{
                durationInMinutes: formData.durationInMinutes,
                timeInterval: formData.timeInterval,
                calendarPayloadDto: formData.calendarPayloadDto
              }}
            />
          </div>

          <div className="lg:col-span-2 flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-[#0F172A]/90 border border-slate-800 rounded-3xl mt-4 shadow-2xl backdrop-blur-xl">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-600/20 border border-red-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-red-900/20">
                      <ShieldCheck className="text-red-400" size={24} />
                    </div>
                    <div>
                        <h4 className="text-white font-black uppercase text-sm italic tracking-widest">Confirm Flight Operation</h4>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Anunțul va fi vizibil pe marketplace după validare.</p>
                    </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full md:w-auto">
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => navigateTo("dashboard")}
                      className="flex-1 md:flex-none h-12 px-6 rounded-xl font-bold uppercase text-xs text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all border border-slate-800/80"
                    >
                      Renunță
                    </Button>
                    <Button
                      type="button"
                      disabled={loading}
                      onClick={handleDraftClick}
                      className="flex-1 md:flex-none h-12 px-6 rounded-xl font-black uppercase tracking-wider text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 shadow-lg active:scale-95 transition-all flex items-center gap-2"
                    >
                      <FileText size={15} className="text-slate-400" />
                      Salvează Draft
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 md:flex-none h-12 px-8 rounded-xl font-black uppercase italic tracking-wider text-xs bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white shadow-lg shadow-red-900/40 hover:shadow-red-600/30 active:scale-95 transition-all"
                    >
                      {isEditMode ? "Salvează Schimbările" : "Publică Experiența"}
                    </Button>
                </div>
          </div>
        </form>
      </div>
      <ConfirmationAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => executeSubmit(false)}
        dict={alertDict}
      />
      <ConfirmationAlert
        isOpen={isDraftAlertOpen}
        onClose={() => setIsDraftAlertOpen(false)}
        onConfirm={() => executeSubmit(true)}
        dict={draftAlertDict}
      />
    </div>
  );
};

export default Experience;